import { flattenAndFilterArrays } from "@/utils/scroll-context"
import axios from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRef, useState } from "react"
import { DropzoneState, useDropzone } from "react-dropzone"
import { useInfiniteQuery } from "react-query"
import Masonry, {
	ResponsiveMasonry,
} from "react-responsive-masonry"
import { PopupContainer } from "@kartyom/shared-components"
import {
	DropAreaForModel,
	getFormatsFromMimeTypes,
	mimeTypes,
	MimeTypes,
} from "../../comps/simple-drop-area"
import { SizedSkeleton } from "../../comps/skeleton"
import {
	ChatRequest,
	ChatResponseEditSegmentsType,
	MediasFullType,
} from "../../controllers/dashboard"
import { useLocalstorageGetter } from "../../hooks/localstorage-getter"
import {
	ChatRequestImage,
	ChatRequestVideo,
} from "../../pages/api/wizard-list-chat"
import { SearchStockRequest } from "../../pages/api/wizard-search-stock"
import { WizardUploadResponse } from "../../pages/api/wizard-upload"
import useInfiniteScroll from "../../utils/infinite"
import {
	getLoadedImage,
	getLoadedVideo,
} from "../../utils/video"
import { emptyResult } from "../dashboard/icons"
import {
	EditSegmentsHeader,
	EditSegmentsMediaItem,
	Footer,
} from "./edit-segments-comps"
import { EditSegmentsContent } from "./edit-segments-content"
import { EditSegmentsAction } from "./response-message"

type ChatRequestEmpty = {
	media_type: "empty"
	index: number
	hint: string
}

export type SelectedMediaType =
	| ChatRequestImage
	| ChatRequestVideo
	| ChatRequestEmpty

interface EditSegmentsPopupProps {
	initialSelected: EditSegmentsAction
	closePopup: () => void
	data: ChatResponseEditSegmentsType
	sendSelectedMedias: (req: ChatRequest) => void
}

export function EditSegmentsPopup(
	props: EditSegmentsPopupProps,
) {
	const {
		data,
		sendSelectedMedias,
		closePopup,
		initialSelected,
	} = props
	const [selected, setSelected] =
		useState<EditSegmentsAction>(initialSelected)
	const [selectedMedias, setSelectedMedias] = useState<
		SelectedMediaType[]
	>(initializeMedias(data.segments))
	const [selectedItem, setSelectedItem] = useState<
		number | null
	>(1)
	const [stockRequest, setStockRequest] =
		useState<SearchStockRequest>({
			query: data.stock.queries[0],
			limit: 10,
			offset: 0,
			orientation: null,
			media_type: "image",
		})
	const lastRowRef = useRef<HTMLDivElement>(null)
	const scrollableParentRef = useRef<HTMLDivElement>(null)
	const [stockLoading, setStockLoading] = useState<
		"loading" | "no_result" | "result"
	>("loading")
	const [uploadedMedias, setUploadedMedias] =
		useState<MediasFullType>([])
	const [mediaTypeFilter, setMediaTypeFilter] = useState<
		"image" | "video" | "all"
	>("all")
	const [storedSearches, setStoreSearches] =
		useLocalstorageGetter<string[]>(
			"edit-segments-search-history",
			[],
		)

	const stockQuery = useInfiniteQuery({
		queryKey: [
			"stock-content",
			stockRequest.media_type,
			stockRequest.orientation,
			stockRequest.query,
		],
		queryFn: ({ pageParam }) =>
			getStockContent(pageParam ?? 0),
		getNextPageParam: (last, all) => {
			if (last.length !== 10) {
				return undefined
			}
			return all.reduce((acc, curr) => acc + curr.length, 0)
		},
		enabled:
			selected === "txt_stock_media" &&
			stockRequest.query !== "",
	})

	useInfiniteScroll(
		stockQuery,
		lastRowRef,
		100,
		selected === "txt_stock_media" &&
			stockRequest.query !== "",
		scrollableParentRef.current,
	)

	const stockContent: MediasFullType =
		flattenAndFilterArrays(
			stockQuery.data?.pages ?? [],
			(x) => x,
			(x) => x.resource_url,
		)

	function addNewSearch(search: string) {
		setStoreSearches((o) => [search, ...o.slice(0, 9)])
	}

	function searchStock(query: string) {
		setStockRequest((o) => {
			return {
				...o,
				query:
					query === ""
						? data.stock.queries[(selectedItem ?? 1) - 1]
						: query,
			}
		})
	}

	async function getStockContent(offset: number) {
		const result = await axios
			.post<{ medias: MediasFullType }>(
				"/api/wizard-search-stock",
				{
					...stockRequest,
					offset,
				},
			)
			.then((res) => res.data)

		setStockLoading(
			result.medias.length === 0 ? "no_result" : "result",
		)
		return result.medias
	}

	function selectOrientation(
		v: "portrait" | "landscape" | null,
	) {
		if (v === stockRequest.orientation) {
			return
		}
		setStockLoading("loading")
		setStockRequest((o) => {
			return {
				...o,
				orientation: v,
			}
		})
	}

	function chooseStockMediaType(type: "image" | "video") {
		if (type === stockRequest.media_type) {
			return
		}
		setStockLoading("loading")
		setStockRequest((o) => {
			return {
				...o,
				media_type: type,
			}
		})
	}

	function replaceChoosenMedia(media: SelectedMediaType) {
		setSelectedMedias((old) =>
			old.map((item, index) => {
				if (item.index === selectedItem) {
					return {
						...media,
						hint: data.segments[index].media.description,
						index: item.index,
					}
				}
				return {
					...item,
					hint: data.segments[index].media.description,
				}
			}),
		)
		setSelectedItem((o) => {
			if (o && o + 1 > data.segments.length) {
				return null
			} else {
				return (o ?? 1) + 1
			}
		})
	}

	function removeSelected(index: number) {
		setSelectedMedias((o) =>
			o.map((item) => {
				if (item.index === index) {
					return {
						media_type: "empty",
						hint: item.hint ?? "",
						index,
					}
				}
				return item
			}),
		)
		setSelectedItem(index)
	}

	const dropzoneState = useDropzone({
		accept: getFormatsFromMimeTypes([
			...mimeTypes["images"],
			...mimeTypes["videos"],
		]),
		onDrop,
		maxFiles: 10,
		noClick: false,
	})

	async function onDrop(files: File[]) {
		files.forEach(async (file) => {
			const isImage = file.type.includes("image")
			const extension = file.type.split("/").at(-1)
			const index = Math.random()

			if (isImage) {
				const imageElem = await getLoadedImage(file)

				const initialUrl = URL.createObjectURL(file)

				setUploadedMedias((o) => [
					{
						media_type: "image",
						resource_url: initialUrl,
						aspect: imageElem.width / imageElem.height,
						loading: true,
						index,
					},
					...o,
				])

				const url = await axios
					.post<WizardUploadResponse>(
						"/api/wizard-upload",
						{ extension },
					)
					.then((r) => r.data)
					.catch(() => console.error("upload faile case"))

				await axios.put(url?.upload ?? "", file)

				const resource_url = url?.download ?? initialUrl

				setUploadedMedias((o) =>
					o.map((elem) => {
						if (elem.index === index) {
							return {
								media_type: "image",
								resource_url,
								aspect: imageElem.width / imageElem.height,
								failed:
									url?.download === undefined
										? true
										: undefined,
								index,
							}
						}
						return elem
					}),
				)

				findAndSyncLoadingFromSelectedMedias(
					initialUrl,
					resource_url,
				)
			} else {
				const video = await getLoadedVideo(file)

				const initialUrl = URL.createObjectURL(file)

				setUploadedMedias((o) => [
					{
						media_type: "video",
						resource_url: initialUrl,
						aspect: video.videoWidth / video.videoHeight,
						thumb_url: null,
						loading: true,
						index,
					},
					...o,
				])

				const url = await axios
					.post<WizardUploadResponse>(
						"/api/wizard-upload",
						{ extension },
					)
					.then((r) => r.data)
					.catch(() => console.error("upload faile case"))

				await axios.put(url?.upload ?? "", file)

				const resource_url = url?.download ?? initialUrl

				setUploadedMedias((o) =>
					o.map((elem) => {
						if (elem.index === index) {
							return {
								media_type: "video",
								resource_url,
								aspect:
									video.videoWidth / video.videoHeight,
								thumb_url: null,
								failed:
									url?.download === undefined
										? true
										: undefined,
								index,
							}
						}
						return elem
					}),
				)
				findAndSyncLoadingFromSelectedMedias(
					initialUrl,
					resource_url,
				)
			}
		})
	}

	function findAndSyncLoadingFromSelectedMedias(
		oldUrl: string,
		newUrl: string,
	) {
		setSelectedMedias((medias) =>
			medias.map((m) => {
				if (
					m.media_type === "empty" ||
					m.resource_url !== oldUrl
				) {
					return m
				} else {
					return {
						...m,
						resource_url: newUrl,
						loading: false,
					}
				}
			}),
		)
	}

	return (
		<PopupContainer open={true} close={closePopup}>
			<div
				className={clsx(
					"flex h-[100svh] w-screen flex-col bg-color-popup-cell",
					"desktop:h-[700px] desktop:w-[1140px] desktop:rounded-[10px]",
				)}>
				<EditSegmentsHeader
					selected={selected}
					setSelected={setSelected}
					closePopup={closePopup}
					hasWebsite={data.website.status === "loaded"}
				/>

				<div
					className={clsx(
						"flex h-[calc(100%-253px)] flex-col bg-color-surface",
						"tablet:h-[calc(100%-179px)]",
					)}>
					<EditSegmentsContent
						selected={selected}
						websiteUrl={
							data.website.status === "loaded"
								? data.website.url
								: ""
						}
						hasUploadedFiles={uploadedMedias.length !== 0}
						selectedOrientation={stockRequest.orientation}
						stockMediaType={stockRequest.media_type}
						relatedSearch={
							data.stock.queries[(selectedItem ?? 1) - 1]
						}
						mediaTypeFilter={mediaTypeFilter}
						dropzoneState={dropzoneState}
						storedSearches={storedSearches}
						addNewSearch={addNewSearch}
						setMediaTypeFilter={setMediaTypeFilter}
						setStockLoading={() =>
							setStockLoading("loading")
						}
						chooseStockMediaType={chooseStockMediaType}
						selectOrientation={selectOrientation}
						searchStock={searchStock}
					/>

					<div
						ref={scrollableParentRef}
						className={clsx(
							selected === "lbl_upload" &&
								"h-[calc(100%-76px)] tablet:h-[calc(100%-80px)]",
							selected === "txt_stock_media" &&
								"h-[calc(100%-160px)] tablet:h-[calc(100%-114px)]",
							selected === "txt_website_images" &&
								"h-[calc(100%-72px)] tablet:h-[calc(100%-100px)]",
							"no-scrollbar overflow-y-scroll px-[16px]",
							"flex flex-col gap-[20px] bg-color-surface",
						)}>
						<RenderMedias
							selected={selected}
							website={data.website}
							stockContent={stockContent}
							stockLoading={stockLoading}
							uploadedMedias={uploadedMedias}
							mediaTypeFilter={mediaTypeFilter}
							dropzoneState={dropzoneState}
							replaceChoosenMedia={replaceChoosenMedia}
						/>

						<div ref={lastRowRef} />
					</div>
				</div>

				<Footer
					selectedMedias={selectedMedias}
					maxUploadCount={data.segments.length}
					currentUploadCount={
						selectedMedias.filter(
							(e) => e.media_type !== "empty",
						).length
					}
					selectedItem={selectedItem}
					setSelectedItem={setSelectedItem}
					sendSelectedMedias={sendSelectedMedias}
					removeSelected={removeSelected}
					closePopup={closePopup}
				/>
			</div>
		</PopupContainer>
	)
}

function initializeMedias(
	segments: ChatResponseEditSegmentsType["segments"],
): SelectedMediaType[] {
	return segments.map((segment, index) => {
		if (segment.media.selection) {
			return {
				...segment.media.selection,
				index: index + 1,
				hint: segment.media.description,
			}
		}
		return {
			media_type: "empty",
			index: index + 1,
			hint: segment.media.description,
		}
	})
}

interface RenderMediasProps {
	selected: EditSegmentsAction
	website: ChatResponseEditSegmentsType["website"]
	stockContent: MediasFullType
	stockLoading: "loading" | "no_result" | "result"
	uploadedMedias: MediasFullType
	mediaTypeFilter: "image" | "video" | "all"
	dropzoneState: DropzoneState
	replaceChoosenMedia: (v: SelectedMediaType) => void
}

function RenderMedias(props: RenderMediasProps) {
	const {
		selected,
		stockContent,
		stockLoading,
		website,
		uploadedMedias,
		mediaTypeFilter,
		dropzoneState,
		replaceChoosenMedia,
	} = props
	const { t } = useTranslation()

	switch (selected) {
		case "lbl_upload":
			const formats = [
				"image/jpeg",
				"image/png",
				"video/mp4",
				"image/webp",
			] as MimeTypes[]

			const backgroundImage = clsx(
				`url("data:image/svg+xml,%3csvg width='100%25' height='100%25'`,
				`xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25'`,
				`fill='none' rx='8' ry='8' stroke='%23B699FA' stroke-width='5'`,
				`stroke-dasharray='6%2c 14' stroke-dashoffset='14' stroke-linecap='round'/%3e%3c/svg%3e")`,
			)
			return (
				<>
					{uploadedMedias.length === 0 ? (
						<div className="flex h-full w-full items-center justify-center">
							<DropAreaForModel
								state={dropzoneState}
								openExplorer={dropzoneState.open}
								border={true}
								formats={formats}
								className={clsx(
									"h-[168px] w-full rounded-[8px]",
									"max-w-[318px] tablet:max-w-[474px]",
								)}
								backgroundImage={backgroundImage}
							/>
						</div>
					) : (
						<ResponsiveMasonry
							columnsCountBreakPoints={{
								1: 2,
								500: 3,
								700: 4,
								1200: 5,
							}}>
							<Masonry gutter="13px">
								{uploadedMedias
									.filter(
										(m) =>
											m.media_type === mediaTypeFilter ||
											mediaTypeFilter === "all",
									)
									.map((m) => (
										<button
											key={m.index}
											onClick={() => replaceChoosenMedia(m)}
											className="group relative flex max-h-[375px] w-full items-center justify-center">
											<EditSegmentsMediaItem {...m} />
										</button>
									))}
							</Masonry>
						</ResponsiveMasonry>
					)}
				</>
			)

		case "txt_website_images":
			if (website.status === "not_loaded") {
				return <></>
			}

			return (
				<ResponsiveMasonry
					columnsCountBreakPoints={{
						1: 2,
						500: 3,
						700: 4,
						1200: 5,
					}}>
					<Masonry gutter="13px">
						{website.medias.map((m) => (
							<button
								key={m.resource_url}
								onClick={() => replaceChoosenMedia(m)}
								className="group relative flex max-h-[375px] w-full items-center justify-center">
								<EditSegmentsMediaItem {...m} />
							</button>
						))}
					</Masonry>
				</ResponsiveMasonry>
			)

		case "txt_stock_media":
			switch (stockLoading) {
				case "loading":
					return (
						<ResponsiveMasonry
							columnsCountBreakPoints={{
								1: 2,
								500: 3,
								700: 4,
								1200: 5,
							}}>
							<Masonry gutter="13px">
								{[
									260, 210, 170, 200, 230, 160, 240, 260,
									150, 170,
								].map((n, index) => (
									<SizedSkeleton
										key={index}
										className="rounded-[6px]"
										style={{ height: n }}
									/>
								))}
							</Masonry>
						</ResponsiveMasonry>
					)

				case "no_result":
					return (
						<div
							style={{}}
							className={clsx(
								"flex h-full flex-col items-center justify-center",
								"gap-[8px] [--icon-color:var(--color-blue-300)]",
							)}>
							{emptyResult()}
							<span className="text-[16px] font-500 text-blue-500">
								{t("txt_no_result")}
							</span>
						</div>
					)

				case "result":
					return (
						<ResponsiveMasonry
							columnsCountBreakPoints={{
								1: 2,
								500: 3,
								700: 4,
								1200: 5,
							}}>
							<Masonry gutter="13px">
								{stockContent.map((m) => (
									<button
										key={m.resource_url}
										onClick={() => replaceChoosenMedia(m)}
										className="group relative flex max-h-[375px] w-full items-center justify-center">
										<EditSegmentsMediaItem {...m} />
									</button>
								))}
							</Masonry>
						</ResponsiveMasonry>
					)
			}
	}
}
